import { useEffect, useContext, useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { AppContext } from '../../context/AppContext';
import { OrderConfirmationPaymentContext } from '../../context/OrderConfirmationPaymentContext';
import { api, CHECKOUT_API_BASE_URL } from '../../services/api';

const StripeConnectModal = ({ open, onClose, onSuccess }) => {
  const { storeId } = useContext(AppContext);
  const { refetchPaymentProviders } = useContext(OrderConfirmationPaymentContext);
  const [connectError, setConnectError] = useState('');

  const handleStripeConnectSuccess = useCallback(
    async (event) => {
      if (event?.data && typeof event.data === 'object' && event.data.type === 'stripeConnected') {
        await refetchPaymentProviders();
        onSuccess?.();
        onClose();
      }
    },
    [refetchPaymentProviders, onSuccess, onClose]
  );

  useEffect(() => {
    window.addEventListener('message', handleStripeConnectSuccess);
    return () => {
      window.removeEventListener('message', handleStripeConnectSuccess);
    };
  }, [handleStripeConnectSuccess]);

  const extractConnectUrl = (payload) => {
    if (!payload) return null;
    if (typeof payload === 'string') return payload;
    return (
      payload.url ||
      payload.redirectUrl ||
      payload.redirect_url ||
      payload.connectUrl ||
      payload.connect_url ||
      payload.data?.url ||
      payload.data?.redirectUrl ||
      payload.data?.redirect_url ||
      null
    );
  };

  const handleConnect = async () => {
    setConnectError('');
    if (storeId == null) {
      setConnectError('Store is not available.');
      return;
    }
    if (!CHECKOUT_API_BASE_URL) {
      setConnectError('Checkout API URL is not configured.');
      return;
    }

    const newWindow = window.open('', '_blank');

    try {
      const response = await api.getStripeConnectUrl(storeId);

      if (response && response.succeeded === false) {
        if (newWindow) newWindow.close();
        setConnectError(response.error_message || 'Failed to start Stripe connect.');
        return;
      }

      const connectUrl = extractConnectUrl(response);

      if (!connectUrl) {
        if (newWindow) newWindow.close();
        setConnectError('Could not retrieve Stripe connect URL.');
        return;
      }

      if (newWindow) {
        newWindow.location.href = connectUrl;
      } else {
        window.location.href = connectUrl;
      }
    } catch (error) {
      if (newWindow) newWindow.close();
      setConnectError(error?.message || 'Failed to start Stripe connect.');
    }
  };

  const handleClose = () => {
    setConnectError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Connect with Stripe
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Before you can receive your first payment with Stripe, you need to connect your account.
        </Typography>
        {connectError ? (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {connectError}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          onClick={handleConnect}
          variant="contained"
          sx={{
            backgroundColor: '#326EEA',
            color: '#fff',
          }}
          endIcon={<OpenInNewIcon />}
        >
          Connect with Stripe
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StripeConnectModal;
